import { Camera, RotateCcw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { validateAndUploadFiles } from "base/file_uploader";

const MAX_PHOTO_WIDTH = 1280;
const PHOTO_QUALITY = 0.85;

/* Live camera only (getUserMedia) so that agents can't pick an old photo from the gallery */
function ShopPhotoCapture({ customer, is_replace, onCancel, onPhotoUploaded }) {
	const video_ref = useRef();
	const stream_ref = useRef();
	const ctx = useRef({}).current;
	const [error, setError] = useState(null);
	const [photo, setPhoto] = useState(null);
	const [is_uploading, setUploading] = useState(false);

	const stopCamera = () => {
		stream_ref.current?.getTracks().forEach((track) => track.stop());
		stream_ref.current = null;
	};

	const startCamera = async () => {
		setError(null);
		if (!navigator.mediaDevices?.getUserMedia) {
			setError("Camera is not supported on this browser. Please open the app in Chrome or Safari.");
			return;
		}
		try {
			stopCamera();
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: "environment" } },
				audio: false,
			});
			stream_ref.current = stream;
			if (video_ref.current) {
				video_ref.current.srcObject = stream;
			}
		} catch (err) {
			setError(
				err?.name === "NotAllowedError"
					? "Allow camera access to take the shop photo. You can enable it from your browser settings."
					: "Couldn't open the camera. Please try again.",
			);
		}
	};

	useEffect(() => {
		startCamera();
		return () => {
			stopCamera();
			ctx.preview_url && URL.revokeObjectURL(ctx.preview_url);
		};
	}, []);

	const capture = () => {
		const video = video_ref.current;
		if (!video?.videoWidth) return;
		const scale = Math.min(1, MAX_PHOTO_WIDTH / video.videoWidth);
		const canvas = document.createElement("canvas");
		canvas.width = Math.round(video.videoWidth * scale);
		canvas.height = Math.round(video.videoHeight * scale);
		canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
		canvas.toBlob((blob) => {
			if (!blob) return;
			ctx.preview_url && URL.revokeObjectURL(ctx.preview_url);
			ctx.preview_url = URL.createObjectURL(blob);
			ctx.uploaded_url = null;
			setPhoto({
				file: new File([blob], `shop_${customer._id}_${Date.now()}.jpg`, { type: "image/jpeg" }),
				preview_url: ctx.preview_url,
			});
		}, "image/jpeg", PHOTO_QUALITY);
	};

	const submitPhoto = async (url) => {
		ctx.uploaded_url = url;
		try {
			await onPhotoUploaded(url);
		} finally {
			setUploading(false);
		}
	};

	const usePhoto = () => {
		if (is_uploading || !photo) return;
		setUploading(true);
		/* check-in can fail after upload (e.g. GPS), don't re-upload the same photo on retry */
		if (ctx.uploaded_url) {
			submitPhoto(ctx.uploaded_url);
			return;
		}
		validateAndUploadFiles({
			file_data_objs: [photo.file],
			allowed_mime_types: ["image/jpeg"],
			no_compress: true,
			onFilesUploaded: ([uploaded_file]) => submitPhoto(uploaded_file.url),
			onUploadError: () => setUploading(false),
		});
	};

	return (
		<div className="tw-absolute tw-inset-0 tw-z-20 tw-flex tw-flex-col tw-bg-black tw-text-white">
			<div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3">
				<X size={22} className="tw-cursor-pointer" onClick={() => !is_uploading && onCancel()} />
				<div>
					<div className="tw-text-xs tw-opacity-80">{is_replace ? "New photo for" : "Check in at"} {customer.name}</div>
					<div className="tw-font-bold">Take a photo of the shop</div>
				</div>
			</div>

			<div className="tw-relative tw-grow tw-overflow-hidden tw-flex tw-items-center tw-justify-center">
				<video
					ref={video_ref}
					autoPlay={true}
					playsInline={true}
					muted={true}
					className={`tw-w-full tw-h-full tw-object-cover ${photo || error ? "tw-hidden" : ""}`}
				/>
				{photo ? <img src={photo.preview_url} alt="Shop" className="tw-w-full tw-h-full tw-object-contain" /> : null}
				{error ? (
					<div className="tw-px-6 tw-text-center tw-text-sm">
						<p className="tw-mb-4">{error}</p>
						<button className="tw-px-4 tw-py-2 tw-rounded-lg tw-bg-white tw-text-black tw-font-bold" onClick={startCamera} type="button">
							Try again
						</button>
					</div>
				) : null}
			</div>

			<div className="tw-px-4 tw-pt-4 tw-pb-6">
				{photo ? (
					<div className="tw-grid tw-grid-cols-[2fr_3fr] tw-gap-3">
						<button
							className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3 tw-rounded-xl tw-border tw-border-white tw-font-bold disabled:tw-opacity-50"
							disabled={is_uploading}
							onClick={() => setPhoto(null)}
							type="button"
						>
							<RotateCcw size={16} />
							Retake
						</button>
						<button
							className="tw-py-3 tw-rounded-xl tw-bg-yellow-500 tw-text-black tw-font-bold disabled:tw-opacity-60"
							disabled={is_uploading}
							onClick={usePhoto}
							type="button"
						>
							{is_uploading ? "Saving..." : is_replace ? "Use this photo" : "Use photo & check in"}
						</button>
					</div>
				) : !error ? (
					<div className="tw-flex tw-justify-center">
						<button
							aria-label="Capture photo"
							className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-white tw-text-black tw-flex tw-items-center tw-justify-center tw-ring-4 tw-ring-white tw-ring-opacity-40"
							onClick={capture}
							type="button"
						>
							<Camera size={26} />
						</button>
					</div>
				) : null}
			</div>
		</div>
	);
}

export default ShopPhotoCapture;
